
import React from 'react';
import { Modal } from '../atoms/BaseComponents';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpModal = ({ isOpen, onClose }: HelpModalProps) => {
  const sections = [
    {
      title: 'General',
      rows: [
        { syntax: '-', description: 'Matches any value (wildcard).', example: '-' },
        { syntax: 'Value', description: 'Exact match equality.', example: '100' },
      ],
    },
    {
      title: 'Strings',
      rows: [
        { syntax: '"Text"', description: 'String literals must be in double quotes.', example: '"Full Time"' },
      ],
    },
    {
      title: 'Numbers (Input only)',
      rows: [
        { syntax: '>, <, >=, <=', description: 'Comparisons.', example: '> 10' },
        { syntax: '[min..max]', description: 'Inclusive range.', example: '[1..10]' },
        { syntax: '(min..max)', description: 'Exclusive range.', example: '(0..5)' },
        { syntax: 'val1, val2', description: 'List of specific values.', example: '1, 5, 10' },
      ],
    },
    {
      title: 'Booleans & Dates',
      rows: [
        { syntax: 'true, false', description: 'Boolean literals.', example: 'true' },
        { syntax: 'YYYY-MM-DD', description: 'ISO Date format.', example: '2023-12-25' },
      ],
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="FEEL Syntax Guide">
      <div className="space-y-6">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          The Friendly Enough Expression Language (FEEL) is used for defining decision logic in DMN tables. 
          Use the following patterns in your input cells:
        </p>

        {sections.map((section) => (
          <div key={section.title} className="space-y-2">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
              {section.title}
            </h4>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-100/50 dark:bg-slate-900/50">
                    <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Syntax</th>
                    <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Description</th>
                    <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Example</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {section.rows.map((row, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-mono text-primary dark:text-emerald-400">{row.syntax}</td>
                      <td className="px-3 py-2 text-slate-600 dark:text-slate-400">{row.description}</td>
                      <td className="px-3 py-2 font-mono text-slate-500 dark:text-slate-500 bg-slate-100/20 dark:bg-slate-900/20">{row.example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-100 dark:border-blue-800/50 text-xs text-blue-700 dark:text-blue-300 flex items-start gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <div>
                <strong>Pro Tip:</strong> Use the "-" symbol in any cell to indicate that the specific input value doesn't matter for that rule.
            </div>
        </div>
      </div>
    </Modal>
  );
};
